import React, { useEffect, useState } from "react";

const OPEN_KEY = "EmitterTool:OrbitPanelOpenV1";

function CollapsibleBox({ title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(OPEN_KEY);
      return saved == null ? defaultOpen : saved === "1";
    } catch {
      return defaultOpen;
    }
  });
  useEffect(() => {
    try { localStorage.setItem(OPEN_KEY, open ? "1" : "0"); } catch {}
  }, [open]);
  return (
    <div style={{
      border: "1px solid #334155", borderRadius: 8, marginBottom: 12,
      background: "#0b0f17", maxWidth: "100%", boxSizing: "border-box", overflowX: "hidden",
    }}>
      <div onClick={() => setOpen(v => !v)} style={{
        padding: "8px 10px", background: "#18212f", userSelect: "none", cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "space-between", fontWeight: 600, gap: 8,
      }}>
        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</span>
        <span style={{ opacity: 0.75, flex: "0 0 auto" }}>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div style={{ padding: 10 }}>{children}</div>}
    </div>
  );
}

const labelStyle = { fontSize: 12, opacity: 0.85, overflowWrap: "anywhere" };

function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }
function toNum(v, d = 0) { const n = Number(v); return Number.isFinite(n) ? n : d; }

function Slider({ label, value, onChange, min = 0, max = 10, step = 0.1, suffix = "" }) {
  const [text, setText] = useState(Number.isFinite(value) ? String(value) : String(min));
  useEffect(() => {
    const s = String(Number.isFinite(value) ? value : min);
    if (s !== text) setText(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, min, max]);
  const commit = (raw) => onChange?.(clamp(toNum(raw, value ?? min), min, max));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
      <div style={{ ...labelStyle, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, minWidth: 0 }}>
        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
          {label}{suffix ? ` (${suffix})` : ""}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flex: "0 0 auto" }}>
          <input
            type="number" value={text} min={min} max={max} step={step}
            onChange={(e) => setText(e.target.value)}
            onBlur={(e) => commit(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
            style={{
              width: "clamp(56px, 24%, 96px)", background: "#0b0d12", color: "white",
              border: "1px solid #374151", borderRadius: 6, padding: "4px 6px",
              fontSize: 12, textAlign: "right", boxSizing: "border-box", maxWidth: "100%",
            }}
          />
          {suffix ? <span style={{ opacity: 0.7, whiteSpace: "nowrap" }}>{suffix}</span> : null}
        </div>
      </div>
      <input
        type="range" value={Number.isFinite(value) ? value : min} min={min} max={max} step={step}
        onChange={(e) => onChange?.(toNum(e.target.value, min))}
        style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", accentColor: "#64748b", cursor: "pointer" }}
      />
    </div>
  );
}

function Segmented({ value, onChange, options }) {
  return (
    <div style={{
      display: "grid", gridTemplateColumns: `repeat(${options.length}, 1fr)`,
      background: "#0b0d12", border: "1px solid #374151", borderRadius: 8, overflow: "hidden",
    }}>
      {options.map((opt, i) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value ?? i} onClick={() => onChange?.(opt.value)} title={opt.title || ""}
            style={{
              padding: "6px 8px", fontSize: 12, color: active ? "#e5e7eb" : "#94a3b8",
              background: active ? "#1f2937" : "transparent",
              border: "none", borderRight: i < options.length - 1 ? "1px solid #374151" : "none",
              cursor: "pointer", whiteSpace: "nowrap",
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/** Notify the runtime whenever orbit-related values change. */
function dispatchParticlesPatch(patch) {
  try {
    window.dispatchEvent(
      new CustomEvent("EmitterTool:ParticlesConfig", { detail: patch })
    );
  } catch {}
}

/** Build a compact payload with only orbit-related keys from an ES object. */
function extractOrbitPayload(es) {
  if (!es || typeof es !== "object") return {};
  const keys = [
    "orbitEnabled","orbitDirection","orbitSpeedAbsDps","orbitSpeedDegPerS",
    "orbitLockRadii","orbitRadiusMinM","orbitRadiusMaxM",
    "orbitShape","orbitEllipseRxM","orbitEllipseRyM","orbitEllipseTiltDeg",
    "orbitStartPhaseMode","orbitStartPhaseJitterDeg",
    "orbitApplyMode","orbitApplyPercent",
    "orbitMode","orbitGroupTransform"
  ];
  const out = {};
  for (const k of keys) if (k in es) out[k] = es[k];
  return out;
}

export default function OrbitPanel({
  effectSettings = {},
  setEffectSettings,
  title = "Orbit",
  defaultOpen = false,
}) {
  const setES = (patch) => {
    if (typeof setEffectSettings !== "function") return;
    setEffectSettings((cur) => {
      const base = cur || {};
      const next = typeof patch === "function" ? patch(base) : patch;

      // If this update touches orbit fields, also notify the runtime.
      const orbitKeys = [
        "orbitEnabled","orbitDirection","orbitSpeedAbsDps","orbitSpeedDegPerS",
        "orbitLockRadii","orbitRadiusMinM","orbitRadiusMaxM",
        "orbitShape","orbitEllipseRxM","orbitEllipseRyM","orbitEllipseTiltDeg",
        "orbitStartPhaseMode","orbitStartPhaseJitterDeg",
        "orbitApplyMode","orbitApplyPercent","orbitMode","orbitGroupTransform"
      ];
      if (Object.keys(next).some(k => orbitKeys.includes(k))) {
        const payload = {};
        for (const k of orbitKeys) if (k in next) payload[k] = next[k];
        if (Object.keys(payload).length) dispatchParticlesPatch(payload);
      }

      return { ...base, ...next };
    });
  };

  const {
    orbitEnabled = false,
    orbitDirection = "ccw",
    orbitSpeedAbsDps = 90,
    orbitSpeedDegPerS = (orbitDirection === "ccw" ? 1 : -1) * (orbitSpeedAbsDps ?? 0),

    orbitLockRadii = true,
    orbitRadiusMinM = 1,
    orbitRadiusMaxM = 1,

    orbitShape = "circle",
    orbitEllipseRxM = 1,
    orbitEllipseRyM = 1,
    orbitEllipseTiltDeg = 0,

    orbitStartPhaseMode = "random",
    orbitStartPhaseJitterDeg = 30,

    orbitApplyMode = "all",
    orbitApplyPercent = 100,
  } = effectSettings || {};

  // Keep panel invariants
  useEffect(() => { if (effectSettings?.orbitMode !== "aroundOrigin") setES({ orbitMode: "aroundOrigin" }); }, []);
  useEffect(() => { if (effectSettings?.orbitGroupTransform) setES({ orbitGroupTransform: false }); }, []);
  useEffect(() => {
    const signed = (orbitDirection === "ccw" ? 1 : -1) * (orbitSpeedAbsDps ?? 0);
    if (signed !== orbitSpeedDegPerS) setES({ orbitSpeedDegPerS: signed });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orbitDirection, orbitSpeedAbsDps]);
  useEffect(() => {
    if (!Number.isFinite(orbitSpeedDegPerS)) return;
    const newDir = orbitSpeedDegPerS >= 0 ? "ccw" : "cw";
    const newAbs = Math.abs(orbitSpeedDegPerS);
    if (newDir !== orbitDirection || newAbs !== orbitSpeedAbsDps) {
      setES({ orbitDirection: newDir, orbitSpeedAbsDps: newAbs });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orbitSpeedDegPerS]);

  // NEW: Re-broadcast full orbit payload whenever any orbit-relevant setting changes.
  // This catches imports (external state writes) where setES is not used.
  useEffect(() => {
    const payload = extractOrbitPayload(effectSettings);
    if (!Object.keys(payload).length) return;

    // Emit the full state
    dispatchParticlesPatch(payload);

    // If orbit is enabled, some runtimes need an enable edge; do a short toggle.
    if (payload.orbitEnabled) {
      requestAnimationFrame(() => {
        dispatchParticlesPatch({ orbitEnabled: false });
        requestAnimationFrame(() => {
          dispatchParticlesPatch({ orbitEnabled: true, ...extractOrbitPayload(effectSettings) });
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    effectSettings?.orbitEnabled,
    effectSettings?.orbitDirection,
    effectSettings?.orbitSpeedAbsDps,
    effectSettings?.orbitSpeedDegPerS,
    effectSettings?.orbitLockRadii,
    effectSettings?.orbitRadiusMinM,
    effectSettings?.orbitRadiusMaxM,
    effectSettings?.orbitShape,
    effectSettings?.orbitEllipseRxM,
    effectSettings?.orbitEllipseRyM,
    effectSettings?.orbitEllipseTiltDeg,
    effectSettings?.orbitStartPhaseMode,
    effectSettings?.orbitStartPhaseJitterDeg,
    effectSettings?.orbitApplyMode,
    effectSettings?.orbitApplyPercent,
    effectSettings?.orbitMode,
    effectSettings?.orbitGroupTransform
  ]);

  // NEW: After any preset import, re-emit and nudge if enabled.
  useEffect(() => {
    const onPreset = () => {
      const payload = extractOrbitPayload(effectSettings);
      if (!Object.keys(payload).length) return;
      dispatchParticlesPatch(payload);
      if (payload.orbitEnabled) {
        requestAnimationFrame(() => {
          dispatchParticlesPatch({ orbitEnabled: false });
          requestAnimationFrame(() => {
            dispatchParticlesPatch({ orbitEnabled: true, ...extractOrbitPayload(effectSettings) });
          });
        });
      }
    };
    window.addEventListener("EmitterTool:UI:PresetApplied", onPreset);
    return () => window.removeEventListener("EmitterTool:UI:PresetApplied", onPreset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectSettings]);

  const setRadiusMin = (v) => {
    if (orbitLockRadii) setES({ orbitRadiusMinM: v, orbitRadiusMaxM: v });
    else setES({ orbitRadiusMinM: Math.min(v, orbitRadiusMaxM) });
  };
  const setRadiusMax = (v) => setES({ orbitRadiusMaxM: Math.max(v, orbitRadiusMinM) });
  const toggleLock = (checked) => {
    if (checked) {
      const same = Math.max(orbitRadiusMinM, orbitRadiusMaxM);
      setES({ orbitLockRadii: true, orbitRadiusMinM: same, orbitRadiusMaxM: same });
    } else setES({ orbitLockRadii: false });
  };

  return (
    <CollapsibleBox title={title} defaultOpen={defaultOpen}>
      <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <input
          type="checkbox" checked={!!orbitEnabled}
          onChange={(e) => setES({ orbitEnabled: e.target.checked, orbitMode: "aroundOrigin", orbitGroupTransform: false })}
          style={{ accentColor: "#64748b" }}
        />
        <span style={{ fontSize: 12 }}>Enable orbit (per-particle centre)</span>
      </label>

      <div style={{ marginTop: 10, marginBottom: 8 }}>
        <div style={{ ...labelStyle, marginBottom: 6 }}>Direction</div>
        <Segmented
          value={orbitDirection}
          onChange={(v) => setES({ orbitDirection: v })}
          options={[
            { value: "ccw", label: "Counter-Clockwise", title: "Positive" },
            { value: "cw", label: "Clockwise", title: "Negative" },
          ]}
        />
      </div>

      <Slider
        label="Angular speed"
        value={orbitSpeedAbsDps}
        onChange={(v) => setES({ orbitSpeedAbsDps: v })}
        min={0} max={720} step={1} suffix="°/s"
      />

      <label style={{ display: "flex", alignItems: "center", gap: 8, margin: "8px 0" }}>
        <input type="checkbox" checked={!!orbitLockRadii} onChange={(e) => toggleLock(e.target.checked)} style={{ accentColor: "#64748b" }} />
        <span style={{ fontSize: 12 }}>Lock min & max distance</span>
      </label>

      {orbitLockRadii ? (
        <Slider
          label="Distance"
          value={orbitRadiusMinM}
          onChange={(v) => setES({ orbitRadiusMinM: v, orbitRadiusMaxM: v })}
          min={0} max={5} step={0.1} suffix="m"
        />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 8, marginBottom: 8 }}>
          <Slider label="Distance min" value={orbitRadiusMinM} onChange={setRadiusMin} min={0} max={5} step={0.1} suffix="m" />
          <Slider label="Distance max" value={orbitRadiusMaxM} onChange={setRadiusMax} min={0} max={5} step={0.1} suffix="m" />
        </div>
      )}

      <div style={{ marginTop: 6, marginBottom: 6 }}>
        <div style={{ ...labelStyle, marginBottom: 6 }}>Path shape</div>
        <Segmented
          value={orbitShape}
          onChange={(v) => setES({ orbitShape: v })}
          options={[
            { value: "circle", label: "Circle" },
            { value: "ellipse", label: "Ellipse" },
          ]}
        />
      </div>

      {orbitShape === "ellipse" ? (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 8, marginBottom: 8 }}>
            <Slider label="RX" value={orbitEllipseRxM} onChange={(v) => setES({ orbitEllipseRxM: v })} min={0} max={5} step={0.1} suffix="m" />
            <Slider label="RY" value={orbitEllipseRyM} onChange={(v) => setES({ orbitEllipseRyM: v })} min={0} max={5} step={0.1} suffix="m" />
          </div>
          <Slider label="Tilt" value={orbitEllipseTiltDeg} onChange={(v) => setES({ orbitEllipseTiltDeg: v })} min={-180} max={180} step={1} suffix="°" />
        </>
      ) : null}

      <div style={{ marginTop: 8, ...labelStyle, marginBottom: 6 }}>Start phase</div>
      <Segmented
        value={orbitStartPhaseMode}
        onChange={(v) => setES({ orbitStartPhaseMode: v })}
        options={[
          { value: "aligned", label: "Aligned" },
          { value: "random", label: "Random" },
        ]}
      />
      {orbitStartPhaseMode === "random" ? (
        <Slider
          label="Jitter"
          value={orbitStartPhaseJitterDeg}
          onChange={(v) => setES({ orbitStartPhaseJitterDeg: clamp(v, 0, 360) })}
          min={0} max={360} step={1} suffix="°"
        />
      ) : null}

      <div style={{ marginTop: 10, marginBottom: 6, ...labelStyle }}>Apply to</div>
      <Segmented
        value={orbitApplyMode}
        onChange={(v) => setES({ orbitApplyMode: v })}
        options={[
          { value: "all", label: "All Particles" },
          { value: "percent", label: "% of Particles" },
        ]}
      />
      {orbitApplyMode === "percent" ? (
        <Slider
          label="Affected"
          value={orbitApplyPercent}
          onChange={(v) => setES({ orbitApplyPercent: clamp(v, 0, 100) })}
          min={0} max={100} step={1} suffix="%"
        />
      ) : null}
    </CollapsibleBox>
  );
}

export { OrbitPanel };
